import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Popover,
  Box,
  Typography,
  TextField,
  Collapse,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {
  Edit,
  Delete as DeleteIcon,
  Print,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { QuotationListApi, QuotationDelete } from "../../../Services/Services";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { toast, ToastContainer } from "react-toastify";
import { generatePdf } from "./QuotationPrint";
pdfMake.vfs = pdfFonts.vfs;

export default function QuotationTable() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [open, setOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [expandedQuotation, setExpandedQuotation] = useState(null);

  const [filters, setFilters] = useState({
    to_company: "",
    to_name: "",
    ref_no: "",
    created_at: "",
  });

  const handleClick = () => {
    navigate("/dashboard/quotationForm");
  };

  useEffect(() => {
    const quatationList = async () => {
      try {
        const response = await QuotationListApi();
         console.log("📋 API Response - First quote:", response[0]);
        setQuotations(response);
      } catch (error) {
        console.error("Error fetching:", error);
      }
    };

    quatationList();
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const handleView = (quote) => {
    setSelectedQuote(quote);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      const deleted = await QuotationDelete(id);

      if (Array.isArray(deleted)) {
        toast.success("Quotation deleted successfully!");
        setQuotations(deleted);
      } else {
        console.error("Unexpected response:", deleted);
        toast.error("Failed to delete quotation!");
      }
    } catch (error) {
      console.error("Error deleting quotation:", error);
      toast.error("Error deleting quotation!");
    }
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedQuote(null);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const filteredQuotations = quotations.filter((quote) => {
    return (
      quote.to_company.toLowerCase().includes(filters.to_company.toLowerCase()) &&
      quote.to_name.toLowerCase().includes(filters.to_name.toLowerCase()) &&
      quote.ref_no.toLowerCase().includes(filters.ref_no.toLowerCase())
    );
  });

  return (
    <Box sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1200, mx: "auto" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* 🔍 FILTERS */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={2.5}>
            <TextField
              fullWidth
              label="Search by Company"
              name="to_company"
              value={filters.to_company}
              onChange={handleFilterChange}
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  height: 32,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={2.5}>
            <TextField
              fullWidth
              label="Search by Party Name"
              name="to_name"
              value={filters.to_name}
              onChange={handleFilterChange}
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  height: 32,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={2.5}>
            <TextField
              fullWidth
              label="Search by Ref No"
              name="ref_no"
              value={filters.ref_no}
              onChange={handleFilterChange}
              size="small"
              sx={{
                "& .MuiInputBase-root": {
                  height: 32,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={2.5}>
            <TextField
              fullWidth
              label="Search by Date"
              name="created_at"
              type="date"
              value={filters.created_at}
              onChange={handleFilterChange}
              size="small"
              InputLabelProps={{ shrink: true }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 32,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleClick}
              startIcon={<AddIcon />}
              sx={{
                height: 32,
                fontSize: "0.7rem",
                borderRadius: 0,
              }}
            >
              Quotation
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* 📱 MOBILE VIEW (CARDS) */}
      {isMobile ? (
        <Box sx={{ bgcolor: "background.paper" }}>
          {filteredQuotations.map((quote, index) => (
            <Box key={quote.quotation_id}>
              <Box
                onClick={() =>
                  setExpandedQuotation(
                    expandedQuotation === quote.quotation_id
                      ? null
                      : quote.quotation_id
                  )
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  cursor: "pointer",
                  bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                  "&:active": {
                    bgcolor: "#e3f2fd",
                  },
                  borderLeft: "4px solid #1976d2",
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "#666",
                        minWidth: 25,
                      }}
                    >
                      #{index + 1}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.85rem",
                        flex: 1,
                      }}
                    >
                      {quote.ref_no}
                    </Typography>
                    <Chip
                      label={quote.date}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        bgcolor: "#1976d2",
                        "& .MuiChip-label": { px: 1, color: "#ffffff", },
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "#666",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>
  {quote.to_title ? `${quote.to_title} ` : ""}{quote.to_name.substring(0, 20)}
</span>
                    <span style={{ color: "#999" }}>•</span>
                    <span style={{ fontSize: "0.7rem" }}>
                      {quote.created_at
                        ? new Date(quote.created_at)
                            .toLocaleDateString("en-GB")
                            .replace(/\//g, "-")
                        : "--"}
                    </span>
                  </Typography>
                </Box>

                <IconButton size="small">
                  {expandedQuotation === quote.quotation_id ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              </Box>

              <Collapse
                in={expandedQuotation === quote.quotation_id}
                timeout="auto"
                unmountOnExit
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                    borderLeft: "4px solid #1976d2",
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 0.8,
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        COMPANY
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {quote.to_company}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        CONTACT
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {quote.contact_no}
                      </Typography>
                    </Box>

                    <Box sx={{ gridColumn: "1 / -1" }}>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        EMAIL
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {quote.email}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                  <Box
                    sx={{ display: "flex", justifyContent: "flex-end", gap: 0.6 }}
                  >
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        navigate("/dashboard/quotationForm", {
                          state: { data: quote },
                        });
                        console.log(quote);
                        
                      }}
                      sx={{
                        bgcolor: "#e3f2fd",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: "#bbdefb",
                        },
                      }}
                    >
                      <Edit sx={{ fontSize: "1rem", color: "#1976d2" }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        setAnchorEl(event.currentTarget);
                        setQuoteToDelete(quote.quotation_id);
                      }}
                      sx={{
                        bgcolor: "#ffebee",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: "1rem", color: "#d32f2f" }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        generatePdf(quote);
                      }}
                      sx={{
                        bgcolor: "#f3e5f5",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: "#e1bee7",
                        },
                      }}
                    >
                      <Print sx={{ fontSize: "1rem", color: "#7b1fa2" }} />
                    </IconButton>
                  </Box>
                </Box>
              </Collapse>

              <Divider />
            </Box>
          ))}
        </Box>
      ) : (
        /* 💻 DESKTOP TABLE VIEW (ORIGINAL) */
    <TableContainer component={Paper} sx={{ maxHeight: "88vh", overflow: "auto" }}>
  <Table
    stickyHeader
    size="small"
    sx={{
      minWidth: 1400,  // Increase table width
      "& .MuiTableCell-root": {
        borderBottom: "1px solid #ccc",
        whiteSpace: "nowrap",
        padding: "8px 16px !important",  // More padding
      },
    }}
  >
    <TableHead
      sx={{
        "& th": {
          fontWeight: "bold",
          fontSize: "0.8rem",
          color: "#333",
          backgroundColor: "#f5f5f5",
        },
      }}
    >
      <TableRow>
        <TableCell
          sx={{
            position: "sticky",
            left: 0,
            background: "#f5f5f5",
            zIndex: 10,
            minWidth: 60,  // Fixed width
          }}
          align="center"
        >
          S.No.
        </TableCell>
        <TableCell
          sx={{
            position: "sticky",
            left: 60,  // Adjust according to S.No. width
            background: "#f5f5f5",
            zIndex: 10,
            minWidth: 130,  // Fixed width for Ref No
          }}
          align="center"
        >
          Ref No
        </TableCell>
        <TableCell align="center" sx={{ minWidth: 100 }}>Date</TableCell>
        <TableCell align="center" sx={{ minWidth: 150 }}>To Name</TableCell>
        <TableCell align="center" sx={{ minWidth: 200 }}>Company</TableCell>
        <TableCell align="center" sx={{ minWidth: 120 }}>Contact No.</TableCell>
        <TableCell align="center" sx={{ minWidth: 200 }}>Email</TableCell>
        <TableCell
          align="center"
          sx={{
            position: "sticky",
            right: 0,
            backgroundColor: "#f5f5f5",
            zIndex: 10,
            minWidth: 120,
            boxShadow: "-2px 0 5px -2px rgba(0,0,0,0.2)",
          }}
        >
          Action
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {filteredQuotations.map((quote, qIdx) => (
        <TableRow
          hover
          key={quote.quotation_id}
          sx={{
            backgroundColor: qIdx % 2 === 0 ? "#fafafa" : "#ffffff",
            "&:hover": { 
              backgroundColor: "#e3f2fd !important",
              "& .MuiTableCell-root": {
                backgroundColor: "#e3f2fd !important",
              }
            },
          }}
        >
          <TableCell
            sx={{
              position: "sticky",
              left: 0,
              backgroundColor: "inherit",
              zIndex: 5,
              minWidth: 60,
            }}
            align="center"
          >
            {qIdx + 1}
          </TableCell>
          <TableCell
            sx={{
              position: "sticky",
              left: 60,
              backgroundColor: "inherit",
              zIndex: 5,
              minWidth: 130,
            }}
            align="center"
          >
            {quote.ref_no}
          </TableCell>
          <TableCell align="center" sx={{ minWidth: 100 }}>
            {quote.date}
          </TableCell>
          <TableCell align="center" sx={{ minWidth: 150 }}>
  {quote.to_title ? `${quote.to_title} ` : ""}{quote.to_name}
</TableCell>
          <TableCell align="center" sx={{ minWidth: 200 }}>
            {quote.to_company}
          </TableCell>
          <TableCell align="center" sx={{ minWidth: 120 }}>
            {quote.contact_no}
          </TableCell>
          <TableCell align="center" sx={{ minWidth: 200 }}>
            {quote.email}
          </TableCell>
          <TableCell
            sx={{
              position: "sticky",
              right: 0,
              backgroundColor: "inherit",
              zIndex: 5,
              minWidth: 120,
              boxShadow: "-2px 0 5px -2px rgba(0,0,0,0.1)",
            }}
            align="center"
          >
            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
              <IconButton
                size="small"
                color="primary"
                onClick={() =>
                  navigate("/dashboard/quotationForm", {
                    state: { data: quote },
                  })
                }
                sx={{ p: 0.5 }}
              >
                <Edit sx={{ fontSize: "1rem" }} />
              </IconButton>
              <IconButton
                color="error"
                size="small"
                onClick={(e) => {
                  setAnchorEl(e.currentTarget);
                  setQuoteToDelete(quote.quotation_id);
                }}
                sx={{ p: 0.5 }}
              >
                <DeleteIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
              <IconButton
                size="small"
                color="default"
                onClick={() => generatePdf(quote)}
                sx={{ p: 0.5 }}
              >
                <Print sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Box>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
</TableContainer>
      )}

      {/* ❌ DELETE POPOVER */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setQuoteToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
            <Button
              color="error"
              size="small"
              onClick={async () => {
                await handleDelete(quoteToDelete);
                setAnchorEl(null);
                setQuoteToDelete(null);
              }}
              sx={{ mr: 1 }}
            >
              Yes
            </Button>
            <Button
              size="small"
              onClick={() => {
                setAnchorEl(null);
                setQuoteToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* 👁️ VIEW DIALOG (UNCHANGED) */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            pb: 0,
            fontSize: "1.25rem",
            lineHeight: 1.5,
          }}
        >
          Quotation Details - {selectedQuote?.ref_no}
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 3,
            fontSize: "0.95rem",
            lineHeight: 1.7,
          }}
        >
          {selectedQuote && (
            <Stack spacing={2}>
              <Grid item xs={12} sm={6}>
               <Typography>
  <strong>To:</strong> {selectedQuote.to_title ? `${selectedQuote.to_title} ` : ""}{selectedQuote.to_name} (
  {selectedQuote.to_company})
</Typography>
                <Typography>
                  <strong>Address:</strong> {selectedQuote.address}
                </Typography>
                <Typography>
                  <strong>Date:</strong> {selectedQuote.date}
                </Typography>
                <Typography>
                  <strong>Subject:</strong> {selectedQuote.subject}
                </Typography>
                {selectedQuote.body && (
                  <Typography>
                    <p>To {selectedQuote.to_company},</p>
                    {selectedQuote.body}
                  </Typography>
                )}
              </Grid>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead sx={{ backgroundColor: "#f9f9f9" }}>
                    <TableRow>
                      <TableCell align="center">
                        <strong>Sl. No.</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Description</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Qty</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Rate (₹)</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Total (₹)</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedQuote.items_list.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell align="center">{idx + 1}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell align="right">{item.quantity}</TableCell>
                        <TableCell align="right">{item.rate}</TableCell>
                        <TableCell align="right">{item.amount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography align="right" sx={{ fontWeight: "bold", mt: 1 }}>
                Total Amount: ₹ {selectedQuote.total_amount}
              </Typography>

              <Grid item xs={12} sm={6}>
                <Typography>
                  <strong>GST %:</strong> {selectedQuote.gst_percentage}
                </Typography>
                <Typography>
                  <strong>Validity:</strong> {selectedQuote.validity}
                </Typography>
                <Typography>
                  <strong>PF:</strong> {selectedQuote.pf}
                </Typography>
                <Typography>
                  <strong>Transport/Insurance:</strong>{" "}
                  {selectedQuote.transport_insurance}
                </Typography>
                <Typography>
                  <strong>Delivery:</strong> {selectedQuote.delivery}
                </Typography>
                <Typography>
                  <strong>Warranty:</strong> {selectedQuote.warranty}
                </Typography>
                <Typography>
                  <strong>Inspection Required:</strong>{" "}
                  {selectedQuote.inspection_required ? "Yes" : "No"}
                </Typography>

                {selectedQuote.payment_terms?.length > 0 && (
                  <Box>
                    <Typography sx={{ fontWeight: "bold" }}>
                      Payment Terms:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                      {selectedQuote.payment_terms.map((term, idx) => (
                        <li key={idx}>{term}</li>
                      ))}
                    </Box>
                  </Box>
                )}

                {selectedQuote.scope_of_work?.length > 0 && (
                  <>
                    <Typography sx={{ fontWeight: "bold", mb: 0 }}>
                      Scope of Work:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                      {selectedQuote.scope_of_work.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </Box>
                  </>
                )}

                {selectedQuote.exclusions?.length > 0 && (
                  <Box>
                    <Typography sx={{ fontWeight: "bold" }}>
                      Exclusions:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0 }}>
                      {selectedQuote.exclusions.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </Box>
                  </Box>
                )}
              </Grid>
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
